'use client';
import { useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { Suspense, useEffect, useRef, useState } from 'react';
import Login from 'src/components/app/auth/components/login';
import OtpForm from 'src/components/app/auth/components/otp';
import Password from 'src/components/app/auth/components/password';

import { getCookie, setCookie } from '@/lib/cookies';
import { APIErrorResponseGenerator, isGuestUser } from '@/lib/helper';

import { USER_TYPE } from '@/components/@base/@helpers/types';
import Container from '@/components/@base/container';
import Spinner from '@/components/@base/spinner';
import { showToast } from '@/components/@base/toast/show-toast';
import {
  usePostChangePassword,
  usePostSsoHandoff,
  usePostUserLogin,
  usePostUserValidation,
  usePostValidateCode,
  usePostVerificationCode,
} from '@/components/app/auth/services/hooks';

import { STEP, useAuthStepStore } from '@/store/auth.store';
import useCommonModalStore from '@/store/common-modal-store';
import { useSmartOfferStore } from '@/store/smart-offer.store';

import { GET_ADD_TO_CART_INFO, GET_CART, GET_PROFILE } from '@/constant/query-key';
import { ACADEMY_BASE_URL } from '@/constant/routes';
import { PRECONDITION } from '@/constant/status-codes';
import StorageKey from '@/constant/storage-key';

// The backend fully constructs `redirect_url` (per the sso/handoff contract), but we still
// verify its origin before navigating — the same "never trust the target, default to a safe
// value" posture as `AfterLoginRouteComponent`'s `isAllowedPath` below. A mismatch is treated
// as a spoofed/buggy value, not a fatal error: silently fall back to the academy's own root
// (or `/`, staying on Zhaket, if `ACADEMY_BASE_URL` isn't even configured) instead of the
// untrusted path.
function getSafeAcademyRedirectUrl(url?: string): string {
  if (url && ACADEMY_BASE_URL) {
    try {
      if (new URL(url).origin === new URL(ACADEMY_BASE_URL).origin) {
        return url;
      }
    } catch {
      // malformed URL — fall through to the safe default below
    }
  }
  return ACADEMY_BASE_URL || '/';
}

export default function Auth() {
  const queryClient = useQueryClient();
  const queries = [[GET_PROFILE], [GET_CART], [GET_ADD_TO_CART_INFO]];
  const {
    step,
    setStep,
    username,
    phoneNumber,
    enterByPassword,
    recoveryMode,
    setUserNotExist,
    userNotExist,
    setTwoFactor,
    setEnterByPassword,
  } = useAuthStepStore();
  const router = useRouter();
  const [isLoading, setIsLoading] = useState(false);
  const { mutate: postUserValidation, isPending: postUserValidationPending } =
    usePostUserValidation();
  const { mutate: postUserLogin, isPending: postUserLoginPending } = usePostUserLogin();
  const { mutate: postValidateCode, isPending: postValidateCodePending } =
    usePostValidateCode();
  const { mutate: postVerificationCode, isPending: postVerificationCodePending } =
    usePostVerificationCode();
  const { mutate: postChangePassword, isPending: postChangePasswordPending } =
    usePostChangePassword();
  const { mutate: postSsoHandoff } = usePostSsoHandoff();
  const { setShow } = useCommonModalStore();
  const [afterLoginRoute, setAfterLoginRoute] = useState('/');

  const [isModal, setIsModal] = useState(false);
  const [sentToBale, setSentToBale] = useState(false);
  const { setCurrentPathname } = useSmartOfferStore();

  // Academy SSO (`?target=academy[&redirect_to=...]`) — read once from the URL and kept
  // for the whole flow, threaded verbatim into every auth-completing request below.
  // The frontend never validates these; the backend decides what to do with them.
  const [target, setTarget] = useState<string | undefined>(undefined);
  const [redirectTo, setRedirectTo] = useState<string | undefined>(undefined);
  const ssoHandoffCalledRef = useRef(false);

  // A user who is already logged into Zhaket and arrives with `target` never goes through
  // login/OTP/change-password at all — call the dedicated handoff endpoint once instead.
  useEffect(() => {
    if (!target || ssoHandoffCalledRef.current) {
      return;
    }
    const token = getCookie(StorageKey.TOKEN);
    if (!token || isGuestUser()) {
      return; // not logged in yet — the normal start/login flow below will carry `target`
    }
    ssoHandoffCalledRef.current = true;
    setIsLoading(true);
    postSsoHandoff(
      { target, redirect_to: redirectTo },
      {
        onSuccess: (data) => {
          if (data.accessToken && data.expiresIn) {
            setCookie(StorageKey.TOKEN, data.accessToken, {
              expires: new Date(data.expiresIn * 1000),
              path: '/',
              domain: process.env.NEXT_PUBLIC_WEB_TOKEN_DOMAIN,
            });
          }
          if (data.nextStep === STEP.AUTH_STEP_ACADEMY_REDIRECT) {
            window.location.replace(getSafeAcademyRedirectUrl(data.redirect_url));
            return;
          }
          if (data.nextStep === STEP.AUTH_STEP_PASSWORD_INPUT) {
            setUserNotExist(true);
            setStep(STEP.AUTH_STEP_PASSWORD_INPUT);
            setIsLoading(false);
            return;
          }
          // Unexpected shape from the backend — fail safe, don't strand the user.
          setIsLoading(false);
          router.push(afterLoginRoute);
        },
        onError(e) {
          setIsLoading(false);
          if (e instanceof AxiosError) {
            showToast(APIErrorResponseGenerator(e));
          }
          router.push(afterLoginRoute);
        },
      },
    );
  }, [target, redirectTo, router, afterLoginRoute, setStep, setUserNotExist, postSsoHandoff]);

  // Function to handle redirection after login
  const redirectToFirstStep = (status?: number) => {
    if (step === STEP.AUTH_STEP_START) {
      setTimeout(() => {
        window.location.reload();
      }, 3000);
    } else if (status === PRECONDITION) {
      setStep(STEP.AUTH_STEP_START);
    }
  };

  // Handlers
  const handleSendCode = (userDoesNotExist?: boolean) => {
    postVerificationCode(
      { username: phoneNumber || username },
      {
        onSuccess(data) {
          if (data?.sentWithBaleDriver && !data?.sentWithOtherDrivers) {
            setSentToBale(true);
          }
          if (data.error) {
            showToast({ message: data.error, type: 'error' });
          } else {
            setStep(data.nextStep);
            showToast({ message: data?.message, type: 'success' });
          }
        },
        onError(e) {
          if (e instanceof AxiosError) {
            redirectToFirstStep(e?.response?.status);
            if (userDoesNotExist) {
              setStep(STEP.AUTH_STEP_SEND_CODE);
            }
            showToast(APIErrorResponseGenerator(e));
          }
        },
      },
    );
  };

  const handleUserValidation = async (recaptcha: string, callback: () => void) => {
    const body: any = { username: username || phoneNumber };
    if (recaptcha) {
      body['reCaptcha'] = recaptcha;
    }
    postUserValidation(body, {
      onSuccess(data) {
        if (callback) {
          callback();
        }
        setTwoFactor(data.twoFactorAuth);
        setUserNotExist(!data.userExists);

        if (data?.error) {
          setStep(data.nextStep);
        } else if (recoveryMode) {
          handleSendCode(!data.userExists);
        } else {
          if (data.nextStep === STEP.AUTH_STEP_PASSWORD_INPUT) {
            setEnterByPassword(true);
          } else if (data.nextStep === STEP.AUTH_STEP_SEND_CODE) {
            setEnterByPassword(false);
          }

          if (data.nextStep === STEP.AUTH_STEP_SEND_CODE) {
            handleSendCode(!data.userExists);
          } else {
            setStep(data.nextStep);
          }
        }
        if (data.error) {
          showToast({ message: data.error, type: 'error' });
        }
      },
      onError(e) {
        if (e instanceof AxiosError) {
          redirectToFirstStep(e?.response?.status);
          showToast(APIErrorResponseGenerator(e));
        }
      },
    });
  };

  const handleCodeValidation = (code: string, callback: () => void) => {
    const body: any = { code };
    body['username'] = username || phoneNumber;
    if (target) {
      body['target'] = target;
    }
    if (redirectTo) {
      body['redirect_to'] = redirectTo;
    }
    postValidateCode(body, {
      onSuccess: async (data) => {
        setCookie(StorageKey.TOKEN, data?.accessToken, {
          expires: data.expiresIn ? new Date(data.expiresIn * 1000) : undefined,
          path: '/',
          domain: process.env.NEXT_PUBLIC_WEB_TOKEN_DOMAIN,
        });
        if (data.nextStep === STEP.AUTH_STEP_ACADEMY_REDIRECT) {
          setIsLoading(true);
          window.location.replace(getSafeAcademyRedirectUrl(data.redirect_url));
          return;
        }
        if (target && data.nextStep === STEP.AUTH_STEP_PASSWORD_INPUT) {
          // Unlike the direct-password login path, the user just submitted an OTP *code*
          // here, not a password — there's no prior password value in the form to carry
          // forward. Show the fresh single-field entry, same as `sso/handoff`'s branch.
          setUserNotExist(true);
          setStep(STEP.AUTH_STEP_PASSWORD_INPUT);
        } else if (userNotExist || recoveryMode) {
          setStep(STEP.AUTH_STEP_PASSWORD_INPUT);
        } else {
          setStep(data.nextStep);
          try {
            await Promise.all(
              queries.map((queryKey) => queryClient.refetchQueries({ queryKey })),
            );
          } catch (error) {
            console.error('Error refetching queries:', error);
          }
          setCurrentPathname(afterLoginRoute);
          if (data.userType && data.userType === USER_TYPE.VENDOR && afterLoginRoute === '/') {
            if (data.newDashboard) {
              router.push('/panel');
            } else {
              router.push('/dashboard');
            }
          } else {
            router.push(afterLoginRoute);
          }
        }
      },
      onError(e) {
        if (e instanceof AxiosError) {
          redirectToFirstStep(e?.response?.status);
          callback();
          showToast(APIErrorResponseGenerator(e));
        }
      },
    });
  };

  const handleUserLogin = (password: string, confirmPassword?: string, twoFactor?: string) => {
    if (confirmPassword) {
      handleChangePassword(password, confirmPassword);
    } else {
      const body: any = { username: username || phoneNumber, password };
      if (twoFactor) {
        body['2fa_code'] = twoFactor;
      }
      if (target) {
        body['target'] = target;
      }
      if (redirectTo) {
        body['redirect_to'] = redirectTo;
      }
      postUserLogin(body, {
        onSuccess: async (data) => {
          setCookie(StorageKey.TOKEN, data?.accessToken, {
            expires: data.expiresIn ? new Date(data.expiresIn * 1000) : undefined,
            path: '/',
            domain: process.env.NEXT_PUBLIC_WEB_TOKEN_DOMAIN,
          });
          if (data.nextStep === STEP.AUTH_STEP_ACADEMY_REDIRECT) {
            setIsLoading(true);
            window.location.replace(getSafeAcademyRedirectUrl(data.redirect_url));
            return;
          }
          if (target && data.nextStep === STEP.AUTH_STEP_PASSWORD_INPUT) {
            // The user just submitted a password attempt to `/login`, which is what
            // revealed this account has none set — jump straight to the confirm-password
            // step instead of AUTH_STEP_PASSWORD_INPUT (a no-op step change, since that's
            // already the current step) so the just-typed value doesn't sit unsubmitted
            // until a second, seemingly-redundant click.
            setUserNotExist(true);
            setStep(STEP.AUTH_STEP_CONFIRM_PASSWORD_INPUT);
            return; // wait for the user to confirm a password before the academy handoff continues
          }
          setStep(data.nextStep);
          try {
            await Promise.all(
              queries.map((queryKey) => queryClient.refetchQueries({ queryKey })),
            );
          } catch (error) {
            console.error('Error refetching queries:', error);
          }
          setCurrentPathname(afterLoginRoute);
          if (data.userType && data.userType === USER_TYPE.VENDOR && afterLoginRoute === '/') {
            if (data.newDashboard) {
              router.push('/panel');
            } else {
              router.push('/dashboard');
            }
          } else {
            router.push(afterLoginRoute);
          }
        },
        onError(e) {
          if (e instanceof AxiosError) {
            redirectToFirstStep(e?.response?.status);
            showToast(APIErrorResponseGenerator(e));
          }
        },
      });
    }
  };

  const handleChangePassword = (password: string, confirmPassword: string) => {
    postChangePassword(
      {
        password,
        confirm_password: confirmPassword,
        ...(target ? { target } : {}),
        ...(redirectTo ? { redirect_to: redirectTo } : {}),
      },
      {
        onSuccess: async (data) => {
          if (data.accessToken && data.expiresIn) {
            setCookie(StorageKey.TOKEN, data.accessToken, {
              expires: new Date(data.expiresIn * 1000),
              path: '/',
              domain: process.env.NEXT_PUBLIC_WEB_TOKEN_DOMAIN,
            });
          }
          if (data.nextStep === STEP.AUTH_STEP_ACADEMY_REDIRECT) {
            setIsLoading(true);
            window.location.replace(getSafeAcademyRedirectUrl(data.redirect_url));
            return;
          }
          showToast({ message: data.message, type: 'success' });
          try {
            await Promise.all(
              queries.map((queryKey) => queryClient.refetchQueries({ queryKey })),
            );
          } catch (error) {
            console.error('Error refetching queries:', error);
          }
          router.push(afterLoginRoute);
          if (isModal) {
            setShow(false);
            setIsLoading(false);
          }
        },
        onError(e) {
          if (e instanceof AxiosError) {
            redirectToFirstStep(e?.response?.status);
            showToast(APIErrorResponseGenerator(e));
          }
        },
      },
    );
  };

  useEffect(() => {
    if (step === STEP.AUTH_STEP_SHOW_DASHBOARD) {
      setIsLoading(true);
    }
    if (step === STEP.AUTH_STEP_SHOW_DASHBOARD && isModal) {
      setShow(false);
    }
  }, [step, afterLoginRoute, setShow]);

  if (isLoading && isModal) {
    return <Spinner />;
  }
  if (isLoading && !isModal) {
    return <Spinner className='fixed h-screen! bg-black/55' />;
  }

  return (
    <>
      <Suspense>
        <AfterLoginRouteComponent
          setAfterLoginRoute={setAfterLoginRoute}
          setIsModal={setIsModal}
          setTarget={setTarget}
          setRedirectTo={setRedirectTo}
        />
      </Suspense>
      <Container center className='w-full flex-col gap-7'>
        {(step === STEP.AUTH_STEP_START ||
          step === STEP.AUTH_STEP_GET_MOBILE ||
          step === STEP.AUTH_STEP_GET_EMAIL) && (
          <Login
            onSubmit={handleUserValidation}
            isLoading={postUserValidationPending || postVerificationCodePending}
          />
        )}
        {step === STEP.AUTH_STEP_SEND_CODE && (
          <OtpForm
            handleSendCode={handleSendCode}
            onSubmit={handleCodeValidation}
            isLoading={postValidateCodePending}
            sentToBale={sentToBale}
          />
        )}
        {(step === STEP.AUTH_STEP_PASSWORD_INPUT ||
          step === STEP.AUTH_STEP_CONFIRM_PASSWORD_INPUT) && (
          <Password
            onSubmit={handleUserLogin}
            isLoading={postUserLoginPending || postChangePasswordPending}
          />
        )}
        {isLoading && isModal && <Spinner />}
        {isLoading && !isModal && <Spinner className='fixed h-screen! bg-black/55' />}
      </Container>
    </>
  );
}

function AfterLoginRouteComponent({
  setAfterLoginRoute,
  setIsModal,
  setTarget,
  setRedirectTo,
}: {
  setAfterLoginRoute: React.Dispatch<React.SetStateAction<string>>;
  setIsModal: React.Dispatch<React.SetStateAction<boolean>>;
  setTarget: React.Dispatch<React.SetStateAction<string | undefined>>;
  setRedirectTo: React.Dispatch<React.SetStateAction<string | undefined>>;
}) {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  useEffect(() => {
    // Read once, unvalidated — the backend decides what a `target`/`redirect_to` pair means.
    setTarget(searchParams.get('target') || undefined);
    setRedirectTo(searchParams.get('redirect_to') || undefined);
  }, [searchParams, setTarget, setRedirectTo]);

  useEffect(() => {
    const allowedPathsRegex =
      /^((\/panel.*)|(\/dashboard.*)|(\/web.*)|(\/store.*)|(\/cart)|(\/search.*))$/;
    const search = searchParams.toString();
    const hasVipOrder = searchParams.has('vip-order');
    const isAllowedPath = (url: string) => {
      if (url === '/cart?subscription') {
        return true;
      }

      if (!url.startsWith('/') || url.startsWith('//')) {
        return false;
      }

      return allowedPathsRegex.test(url);
    };
    let route = '/';
    const afterLoginParam = searchParams.get('afterLogin');
    if (afterLoginParam) {
      if (isAllowedPath(afterLoginParam)) {
        route = afterLoginParam;
      } else {
        route = '/';
      }
    } else if (pathname !== '/login') {
      if (allowedPathsRegex.test(pathname)) {
        if (pathname.startsWith('/panel') && hasVipOrder) {
          route = `${pathname}?${search}`;
        } else {
          route = pathname;
        }
        setIsModal(true);
      } else {
        route = '/';
      }
    } else {
      route = '/';
    }

    setAfterLoginRoute(route);
  }, [pathname, searchParams, setAfterLoginRoute, setIsModal]);

  return null;
}
